import type { Achievements } from "@highmarch/app";
import { BalanceData, PlayerProfile, gearScore } from "@highmarch/domain";
import { icbtnHtml } from "../kit";
import { icon } from "../iconsKit";
import { Readiness, ReadinessAction } from "./readiness";
import { secHtml, sheetHeadHtml } from "../components";
import { frameElement } from "../titleFrame";

/**
 * The honours card (WP7): the achievements count and the worn title, with the Achievements sheet and the Almanac one
 * tap away. The achievements feature sets it at boot (features/achievements.ts); without it the card is left out.
 */
export interface CommanderHonours {
  readonly achievements: Achievements;
  openAchievements(parent: HTMLElement): void;
  openAlmanac(parent: HTMLElement): void;
}
let honours: CommanderHonours | null = null;
export function setCommanderHonours(h: CommanderHonours | null): void { honours = h; }
/** The Commander title worn now ("" for none): the Skins picker's frame preview shows it. */
export function commanderTitle(): string { return honours?.achievements.title ?? ""; }
/** MP-27: the Hall of Records' button beside them (features/records.ts sets it; without it the button is left out). */
let openRecords: ((parent: HTMLElement) => void) | null = null;
export function setCommanderRecords(open: ((parent: HTMLElement) => void) | null): void { openRecords = open; }

function honoursHtml(h: CommanderHonours): string {
  const a = h.achievements, rows = a.rows(), done = rows.filter((r) => r.done).length, ready = a.claimable();
  const title = a.title ? `<span class="titleline">${icon("flag", 12)} ${a.title}</span>` : "No title yet";
  return `<div class="honours"><span class="medal ${done > 0 ? "ready" : "todo"}" style="--s:44px">${icon("trophy", 20, done > 0 ? "#5a3606" : "#e9d6b0", 2.4)}</span>
    <div class="ht"><b>Achievements <span class="nw">${done} / ${rows.length}</span></b><small>${title}</small></div>
    <div class="hb">${icbtnHtml("trophy", "hach", "", ready > 0 ? ready : null, 44)}${openRecords ? icbtnHtml("star", "hrec", "", null, 44) : ""}${icbtnHtml("shield", "halm", "", null, 44)}</div></div>`;
}

/**
 * VIP's combat bonuses (docs/SHOP.md §8.3) count in every battle (through the gear bonuses) but not in the power number,
 * and only tower damage counts on the readiness strip ("+n.n VIP"): one line says what the rest is. None at VIP 0.
 */
export function vipCombatHtml(p: PlayerProfile): string {
  const v = p.vip;
  if (v.towerDamagePercent + v.fortressHpPercent + v.troopPercent <= 0) return "";
  return `<p class="vipcombat">${icon("star", 12)} VIP · +${v.towerDamagePercent}% damage · +${v.fortressHpPercent}% fortress · +${v.troopPercent}% troops</p>`;
}

/**
 * Commander sheet (hub redesign): where the power number comes from (fortress, towers, gear — the terms of
 * `powerScore`) and "Get stronger", up to three concrete next steps from the readiness. Opened from the commander chip
 * and from the battle card's readiness strip.
 */
export function showCommander(parent: HTMLElement, B: BalanceData, p: PlayerProfile, level: number, r: Readiness, onAction: (a: ReadinessAction) => void): HTMLElement {
  const sheet = document.createElement("div");
  sheet.className = "sheet panel commander";
  // The same terms as `PlayerProfile.powerScore`, so the three numbers add up to the chip's power.
  const towers = p.towerLevels.reduce((a, l) => a + l * B.meta.powerTowerWeight, 0);
  const fortress = p.baseLevel * B.meta.powerBaseWeight;
  const gear = gearScore(B, p.gear);
  const part = (label: string, v: number) => `<div class="stat"><small>${label}</small><b class="disp">${v}</b></div>`;
  const glyph = (a: ReadinessAction) => (a.kind === "equip" ? "bag" : a.kind === "upgradeTower" ? "tower" : "map");
  const actions = r.actions.length
    ? r.actions.map((a, i) => `<button class="act" data-i="${i}"><span class="ai">${icon(glyph(a), 20, "#fff8e6")}</span><b>${a.label}</b>${icon("chev", 16)}</button>`).join("")
    : `<p class="muted none">Nothing stands in your way. Press PLAY!</p>`;
  sheet.innerHTML = `
    ${sheetHeadHtml({ title: "Commander", sub: `${icon("bolt", 13)} ${p.powerScore} power${honours?.achievements.title ? ` · ${honours.achievements.title}` : ""}`, portrait: icon("flag", 34, "#fff8e6"), tint: "blue" })}
    <div class="stats4 three">${part("FORTRESS", fortress)}${part("TOWERS", towers)}${part("GEAR", gear)}</div>
    ${vipCombatHtml(p)}
    ${honours ? honoursHtml(honours) : ""}
    ${secHtml(`Get stronger for level ${level}`)}
    <div class="acts">${actions}</div>`;
  frameElement(sheet.querySelector<HTMLElement>(".k-head .titles"));           // MP-23: the worn title frame round name and title
  sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
  for (const b of sheet.querySelectorAll<HTMLButtonElement>(".act")) b.onclick = () => { sheet.remove(); onAction(r.actions[Number(b.dataset.i)]); };
  const h = honours;
  if (h) {
    sheet.querySelector<HTMLButtonElement>(".hach")?.setAttribute("aria-label", "Achievements");
    sheet.querySelector<HTMLButtonElement>(".halm")?.setAttribute("aria-label", "Almanac");
    sheet.querySelector<HTMLButtonElement>(".hach")?.addEventListener("click", () => { sheet.remove(); h.openAchievements(parent); });
    sheet.querySelector<HTMLButtonElement>(".halm")?.addEventListener("click", () => { sheet.remove(); h.openAlmanac(parent); });
    const rec = openRecords, recBtn = sheet.querySelector<HTMLButtonElement>(".hrec");
    recBtn?.setAttribute("aria-label", "Hall of Records");
    if (rec) recBtn?.addEventListener("click", () => { sheet.remove(); rec(parent); });
  }
  parent.append(sheet);
  return sheet;
}
